import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Sans:wght@300;400;500&display=swap');
`;

// ── Keyframes
const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.04); }
`;

const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const rotateSlowReverse = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

const scanline = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 0.6; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(500%); opacity: 0; }
`;

// ── Wrapper
const Section = styled.section`
  position: relative;
  background: #020008;
  overflow: hidden;
  padding: 140px 0 160px;
`;

// Grain
const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.035;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

// Ambient orbs
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "100px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.2};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

// Grid lines background
const GridBg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(168, 85, 247, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(168, 85, 247, 0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(
    ellipse 80% 70% at 50% 50%,
    black 0%,
    transparent 100%
  );
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
`;

// ── Hero headline block
const HeroBlock = styled.div`
  text-align: center;
  margin-bottom: 100px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(20px);

  &::before,
  &::after {
    content: "";
    width: 24px;
    height: 1px;
    background: rgba(168, 85, 247, 0.5);
  }
`;

const MainHeadline = styled.h2`
  font-family: "Syne", sans-serif;
  font-size: clamp(2.8rem, 6vw, 5.5rem);
  font-weight: 800;
  line-height: 1.05;
  color: #fff;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(30px);

  span.accent {
    color: transparent;
    -webkit-text-stroke: 1px rgba(168, 85, 247, 0.7);
  }

  span.glow {
    color: #c084fc;
    text-shadow: 0 0 40px rgba(192, 132, 252, 0.4);
  }
`;

const SubHeadline = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto 3rem;
  opacity: 0;
  transform: translateY(20px);
`;

// Stats row
const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(2rem, 5vw, 5rem);
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(20px);
`;

const Stat = styled.div`
  text-align: center;
`;

const StatNum = styled.div`
  font-family: "Syne", sans-serif;
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  font-weight: 800;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.4rem;

  span {
    color: #c084fc;
  }
`;

const StatLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;

const StatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(168, 85, 247, 0.2);

  @media (max-width: 600px) {
    display: none;
  }
`;

// ── Shield visual
const ShieldWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 100px;
  opacity: 0;
  transform: scale(0.9);
`;

const ShieldOuter = styled.div`
  position: relative;
  width: clamp(260px, 35vw, 380px);
  height: clamp(260px, 35vw, 380px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Ring = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px solid ${({ c }) => c || "rgba(168,85,247,0.15)"};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  animation: ${({ rev }) => (rev ? rotateSlowReverse : rotateSlow)}
    ${({ dur }) => dur || "20s"} linear infinite;

  /* Dashed segment */
  ${({ dashed }) =>
    dashed &&
    `
    border-style: dashed;
  `}
`;

const RingDot = styled.div`
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ c }) => c || "#a855f7"};
  box-shadow: 0 0 10px ${({ c }) => c || "#a855f7"};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  animation: ${pulse} 2s ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;

const ShieldCore = styled.div`
  position: relative;
  z-index: 2;
  width: clamp(130px, 18vw, 180px);
  height: clamp(130px, 18vw, 180px);
  display: flex;
  align-items: center;
  justify-content: center;

  /* Shield shape via clip-path */
  background: linear-gradient(
    160deg,
    rgba(124, 58, 237, 0.4),
    rgba(168, 85, 247, 0.2)
  );
  clip-path: polygon(50% 0%, 100% 15%, 100% 60%, 50% 100%, 0% 60%, 0% 15%);
  border: none;

  &::before {
    content: "";
    position: absolute;
    inset: 2px;
    clip-path: polygon(50% 0%, 100% 15%, 100% 60%, 50% 100%, 0% 60%, 0% 15%);
    background: linear-gradient(160deg, #1a0035, #0d0020);
  }
`;

const ShieldIcon = styled.div`
  position: relative;
  z-index: 1;
  font-size: clamp(2.5rem, 4vw, 3.5rem);
`;

const ScanLine = styled.div`
  position: absolute;
  inset: 0;
  clip-path: polygon(50% 0%, 100% 15%, 100% 60%, 50% 100%, 0% 60%, 0% 15%);
  overflow: hidden;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.8),
      transparent
    );
    animation: ${scanline} 3s ease-in-out infinite;
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  bottom: -16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(10, 0, 20, 0.9);
  border: 1px solid rgba(168, 85, 247, 0.4);
  border-radius: 100px;
  padding: 6px 14px;
  white-space: nowrap;

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #c084fc;
  }
`;

const BlinkDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 8px #4ade80;
  animation: ${blink} 1.5s ease-in-out infinite;
`;

// ── Feature grid
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5px;
  margin-bottom: 100px;
  border: 1.5px solid rgba(168, 85, 247, 0.12);
  border-radius: 24px;
  overflow: hidden;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: clamp(1.8rem, 3vw, 2.5rem);
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(40px);
  transition: background 0.3s ease;
  cursor: default;

  /* separator lines via outline on grid gap */
  outline: 1.5px solid rgba(168, 85, 247, 0.08);

  &:hover {
    background: rgba(168, 85, 247, 0.06);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.4),
      transparent
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.25),
    rgba(168, 85, 247, 0.1)
  );
  border: 1px solid rgba(168, 85, 247, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 1.2rem;
`;

const FeatureTitle = styled.h3`
  font-family: "Syne", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.6rem;
  letter-spacing: 0.01em;
`;

const FeatureDesc = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.75;
`;

const FeatureTag = styled.div`
  display: inline-flex;
  margin-top: 1rem;
  padding: 4px 10px;
  border-radius: 100px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.25);
  font-family: "DM Sans", sans-serif;
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a855f7;
`;

// ── CEO strip
const CeoStrip = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  border: 1px solid rgba(168, 85, 247, 0.12);
  border-radius: 24px;
  padding: clamp(2rem, 4vw, 3.5rem);
  background: rgba(255, 255, 255, 0.015);
  opacity: 0;
  transform: translateY(40px);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const CeoLeft = styled.div``;

const CeoLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1rem;
`;

const CeoName = styled.h3`
  font-family: "Syne", sans-serif;
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const CeoTitle = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 1.5rem;
`;

const CeoQuote = styled.blockquote`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.9rem, 1.5vw, 1rem);
  font-weight: 300;
  font-style: italic;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  border-left: 2px solid rgba(168, 85, 247, 0.4);
  padding-left: 1.2rem;
`;

const CeoRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CeoStat = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.4rem;
  border-radius: 16px;
  background: rgba(168, 85, 247, 0.06);
  border: 1px solid rgba(168, 85, 247, 0.12);
`;

const CeoStatIcon = styled.div`
  font-size: 1.4rem;
  flex-shrink: 0;
`;

const CeoStatText = styled.div``;

const CeoStatNum = styled.div`
  font-family: "Syne", sans-serif;
  font-size: 1.3rem;
  font-weight: 800;
  color: #c084fc;
  line-height: 1;
  margin-bottom: 0.2rem;
`;

const CeoStatLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.35);
`;

// ── Features data
const FEATURES = [
  {
    icon: "👻",
    title: "Disappearing Photos",
    desc: "Every photo you share vanishes after it's been seen. No screenshots, no saves, no traces. Your moments stay yours.",
    tag: "Exclusive",
  },
  {
    icon: "🔐",
    title: "End-to-End Encryption",
    desc: "AES-256 encryption on every message — the same standard used by banks and governments. Not even we can read what you send.",
    tag: "Military Grade",
  },
  {
    icon: "🎭",
    title: "Identity Verification",
    desc: "Real-time document scanning with liveness detection. Every profile verified before it goes live. No bots. No fakes. Ever.",
    tag: "Real-Time",
  },
  {
    icon: "📍",
    title: "Blurred Location",
    desc: "We never expose your exact coordinates. You appear within a general area — close enough to connect, private enough to stay safe.",
    tag: "Privacy First",
  },
  {
    icon: "🛡️",
    title: "Screenshot Detection",
    desc: "The moment someone tries to screenshot your content, you're notified instantly. What's shared with you stays with you.",
    tag: "Real-Time Alert",
  },
  {
    icon: "🗑️",
    title: "Right to Disappear",
    desc: "Request full deletion of every piece of your data — messages, photos, profile — processed within 48 hours. No questions asked.",
    tag: "GDPR Ready",
  },
];

export default function CybersecuritySection() {
  const sectionRef = useRef(null);
  const eyebrowRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const statsRef = useRef(null);
  const shieldRef = useRef(null);
  const cardRefs = useRef([]);
  const ceoRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero block
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            end: "top 30%",
            scrub: 0.7,
          },
        })
        .to(eyebrowRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(headlineRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.1)
        .to(subRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2)
        .to(statsRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.3);

      // ── Shield
      gsap
        .timeline({
          scrollTrigger: {
            trigger: shieldRef.current,
            start: "top 80%",
            end: "top 40%",
            scrub: 0.8,
          },
        })
        .to(shieldRef.current, { opacity: 1, scale: 1, ease: "back.out(1.4)" });

      // ── Feature cards stagger
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        gsap
          .timeline({
            scrollTrigger: {
              trigger: card,
              start: "top 88%",
              end: "top 55%",
              scrub: 0.7,
            },
          })
          .to(card, { opacity: 1, y: 0, ease: "power2.out" });
      });

      // ── CEO strip
      gsap
        .timeline({
          scrollTrigger: {
            trigger: ceoRef.current,
            start: "top 85%",
            end: "top 50%",
            scrub: 0.7,
          },
        })
        .to(ceoRef.current, { opacity: 1, y: 0, ease: "power2.out" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Section ref={sectionRef} id="safety">
        <Grain />
        <GridBg />
        <Orb
          s="600px"
          c="rgba(124,58,237,0.18)"
          top="-5%"
          left="-10%"
          op={0.25}
          blur="130px"
        />
        <Orb
          s="500px"
          c="rgba(232,121,249,0.12)"
          top="30%"
          right="-8%"
          op={0.2}
          blur="120px"
        />
        <Orb
          s="400px"
          c="rgba(168,85,247,0.15)"
          bottom="5%"
          left="35%"
          op={0.2}
          blur="100px"
        />

        <Container>
          {/* ── Headline block */}
          <HeroBlock>
            <Eyebrow ref={eyebrowRef}>Built different</Eyebrow>
            <MainHeadline ref={headlineRef}>
              Security isn't a feature.
              <br />
              <span className="accent">It's the</span>{" "}
              <span className="glow">foundation.</span>
            </MainHeadline>
            <SubHeadline ref={subRef}>
              Every line of code, every interaction, every photo — wrapped in
              layers of protection most apps don't even think about. Because
              when trust is everything, half-measures aren't an option.
            </SubHeadline>

            <StatsRow ref={statsRef}>
              <Stat>
                <StatNum>
                  10<span>+</span>
                </StatNum>
                <StatLabel>Years CEO Experience</StatLabel>
              </Stat>
              <StatDivider />
              <Stat>
                <StatNum>
                  40<span>+</span>
                </StatNum>
                <StatLabel>Years Dev Team Experience</StatLabel>
              </Stat>
              <StatDivider />
              <Stat>
                <StatNum>
                  256<span>-bit</span>
                </StatNum>
                <StatLabel>AES Encryption</StatLabel>
              </Stat>
              <StatDivider />
            </StatsRow>
          </HeroBlock>

          {/* ── Shield */}
          <ShieldWrap ref={shieldRef}>
            <ShieldOuter>
              <Ring s="340px" c="rgba(168,85,247,0.08)" dur="35s" dashed />
              <Ring s="270px" c="rgba(168,85,247,0.12)" dur="25s" rev />
              <Ring s="200px" c="rgba(232,121,249,0.15)" dur="18s" />

              <RingDot c="#a855f7" top="0px" left="50%" del="0s" />
              <RingDot c="#e879f9" top="50%" left="0px" del="0.5s" />
              <RingDot c="#c084fc" top="100%" left="50%" del="1s" />
              <RingDot c="#a855f7" top="50%" left="100%" del="1.5s" />

              <ShieldCore>
                <ScanLine />
                <ShieldIcon>🛡️</ShieldIcon>
              </ShieldCore>

              <StatusBadge>
                <BlinkDot />
                <span>All Systems Protected</span>
              </StatusBadge>
            </ShieldOuter>
          </ShieldWrap>

          {/* ── Feature cards */}
          <FeaturesGrid>
            {FEATURES.map((f, i) => (
              <FeatureCard key={i} ref={(el) => (cardRefs.current[i] = el)}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
                <FeatureTag>{f.tag}</FeatureTag>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          {/* ── CEO strip */}
          <CeoStrip ref={ceoRef}>
            <CeoLeft>
              <CeoLabel>The mind behind it</CeoLabel>
              <CeoName>Our CEO</CeoName>
              <CeoTitle>
                Chief Executive Officer & Head of Security Architecture
              </CeoTitle>
              <CeoQuote>
                "I've spent over a decade breaking into systems so I could learn
                how to build ones that can't be broken. Every security feature
                in this app exists because I've personally seen what happens
                when it doesn't."
              </CeoQuote>
            </CeoLeft>
            <CeoRight>
              <CeoStat>
                <CeoStatIcon>🔍</CeoStatIcon>
                <CeoStatText>
                  <CeoStatNum>10+ Years</CeoStatNum>
                  <CeoStatLabel>
                    Cybersecurity expertise — offensive & defensive
                  </CeoStatLabel>
                </CeoStatText>
              </CeoStat>
              <CeoStat>
                <CeoStatIcon>👥</CeoStatIcon>
                <CeoStatText>
                  <CeoStatNum>40+ Years</CeoStatNum>
                  <CeoStatLabel>
                    Combined dev team security experience
                  </CeoStatLabel>
                </CeoStatText>
              </CeoStat>
              <CeoStat>
                <CeoStatIcon>🏆</CeoStatIcon>
                <CeoStatText>
                  <CeoStatNum>Zero Breaches</CeoStatNum>
                  <CeoStatLabel>
                    Track record across all previous deployments
                  </CeoStatLabel>
                </CeoStatText>
              </CeoStat>
            </CeoRight>
          </CeoStrip>
        </Container>
      </Section>
    </>
  );
}
